import React, { useState, useEffect } from 'react';
import { ActivityIndicator } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ListsManager from '../services/ListsManager';
import { FaArrowLeft, FaSave, FaMagic, FaUser, FaPen, FaGlobe, FaCheckCircle } from 'react-icons/fa';
import { useTheme } from '../theme/ThemeContext';

let T = {};

// ── Sub-components ────────────────────────────────────────────────────────────

const SectionLabel = ({ children }) => (
    <div style={{
        color: T.textMuted, fontSize: 10, fontWeight: 700,
        textTransform: 'uppercase', letterSpacing: 1, marginBottom: 10,
    }}>{children}</div>
);

const FieldCard = ({ icon, iconColor, iconBg, title, hint, charCount, children }) => (
    <div style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                    width: 30, height: 30, borderRadius: 9, flexShrink: 0,
                    background: iconBg, display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    {React.cloneElement(icon, { size: 13, color: iconColor })}
                </div>
                <div>
                    <div style={{ color: T.text, fontSize: 13, fontWeight: 700 }}>{title}</div>
                    <div style={{ color: T.textMuted, fontSize: 11, marginTop: 1 }}>{hint}</div>
                </div>
            </div>
            {charCount != null && (
                <span style={{ color: T.textMuted, fontSize: 10, fontFamily: 'monospace' }}>
                    {charCount}
                </span>
            )}
        </div>
        <div style={{
            background: T.card, borderRadius: 14,
            border: `1px solid ${T.border}`,
            padding: '14px 16px',
            transition: 'border-color 0.15s',
        }}>
            {children}
        </div>
    </div>
);

// ── Main screen ───────────────────────────────────────────────────────────────

const WritingStyleScreen = ({ onBack } = {}) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { theme } = useTheme();

    T = {
        bg:        theme.bg,
        card:      theme.bgCard,
        surface:   theme.bgSurface,
        border:    theme.border,
        text:      theme.textPrimary,
        textSec:   theme.textSecondary,
        textMuted: theme.textMuted,
        primary:   '#0a84ff',
        purple:    '#818cf8',
        success:   '#30d158',
        warning:   '#ffd60a',
    };

    const [loading, setLoading]             = useState(true);
    const [saving, setSaving]               = useState(false);
    const [context, setContext]             = useState('');
    const [writingStyle, setWritingStyle]   = useState('');
    const [brandScale, setBrandScale]       = useState('national');
    const [hasChanges, setHasChanges]       = useState(false);
    const [saveSuccess, setSaveSuccess]     = useState(false);
    const [focusedField, setFocusedField]   = useState(null);

    useEffect(() => { loadProfile(); }, []); // eslint-disable-line

    const loadProfile = async () => {
        try {
            setLoading(true);
            const profile = await ListsManager.getListenerProfile();
            if (profile) {
                setContext(profile.context || '');
                setWritingStyle(profile.writing_style || '');
                setBrandScale(profile.brand_scale || 'national');
            }
        } catch (e) {
            console.error('Error loading profile:', e);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            const result = await ListsManager.saveListenerProfile(context, writingStyle, brandScale);
            if (result) {
                setHasChanges(false);
                setSaveSuccess(true);
                setTimeout(() => setSaveSuccess(false), 2500);
            }
        } catch (e) {
            console.error('Error saving profile:', e);
        } finally {
            setSaving(false);
        }
    };

    const embedded = !!onBack;

    const SCALE_OPTIONS = [
        {
            value: 'local',
            label: t('app.writingStyle.scaleLocal'),
            sub: t('app.writingStyle.scaleLocalSub'),
            threshold: '5k+',
            icon: '🏘️',
            color: '#30d158',
        },
        {
            value: 'national',
            label: t('app.writingStyle.scaleNational'),
            sub: t('app.writingStyle.scaleNationalSub'),
            threshold: '100k+',
            icon: '🏙️',
            color: '#0a84ff',
        },
        {
            value: 'global',
            label: t('app.writingStyle.scaleGlobal'),
            sub: t('app.writingStyle.scaleGlobalSub'),
            threshold: '1M+',
            icon: '🌍',
            color: '#bf5af2',
        },
    ];

    const css = `
        .ws-textarea {
            width: 100%; box-sizing: border-box; resize: none;
            background: transparent; border: none; outline: none;
            color: ${T.text}; font-size: 13px; line-height: 21px;
            font-family: inherit; caret-color: ${T.purple};
        }
        .ws-textarea::placeholder { color: ${T.textMuted}; }
        .ws-field-card:focus-within { border-color: ${T.purple}55 !important; box-shadow: 0 0 0 3px ${T.purple}0d; }
        .ws-scale:hover { border-color: ${T.border} !important; filter: brightness(1.07); }
        .ws-scale.active { box-shadow: 0 0 0 3px rgba(10,132,255,0.12); }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(128,128,128,0.2); border-radius: 2px; }
    `;

    if (loading) {
        return (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: T.bg, flexDirection: 'column', gap: 12 }}>
                <div style={{ width: 44, height: 44, borderRadius: 14, background: 'rgba(129,140,248,0.1)', border: '1px solid rgba(129,140,248,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FaMagic size={18} color={T.purple} />
                </div>
                <ActivityIndicator size="small" color={T.purple} />
            </div>
        );
    }

    return (
        <>
            <style dangerouslySetInnerHTML={{ __html: css }} />
            <div style={{ display: 'flex', flexDirection: 'column', height: '100%', background: T.bg, overflow: 'hidden' }}>

                {/* ── Header ── */}
                <div style={{
                    display: 'flex', alignItems: 'center', gap: 14,
                    padding: embedded ? '18px 24px 16px' : '14px 24px',
                    borderBottom: `1px solid ${T.border}`, flexShrink: 0,
                }}>
                    {!embedded && (
                        <button onClick={() => navigate(-1)} style={{ width: 34, height: 34, borderRadius: 10, background: T.surface, border: `1px solid ${T.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                            <FaArrowLeft size={13} color={T.text} />
                        </button>
                    )}
                    <div style={{ width: 36, height: 36, borderRadius: 11, background: 'rgba(129,140,248,0.12)', border: '1px solid rgba(129,140,248,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <FaMagic size={15} color={T.purple} />
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ color: T.text, fontSize: embedded ? 15 : 17, fontWeight: 700, letterSpacing: -0.3 }}>
                            {t('app.writingStyle.title')}
                        </div>
                        <div style={{ color: T.textMuted, fontSize: 11, marginTop: 1 }}>
                            {hasChanges
                                ? <span style={{ color: '#ffd60a' }}>{t('app.writingStyle.unsavedChanges')}</span>
                                : t('app.writingStyle.personalizeVoice')}
                        </div>
                    </div>

                    {/* Save button */}
                    <button
                        onClick={handleSave}
                        disabled={!hasChanges || saving}
                        style={{
                            display: 'flex', alignItems: 'center', gap: 7,
                            padding: '8px 16px', borderRadius: 9, border: 'none',
                            cursor: hasChanges ? 'pointer' : 'default',
                            background: saveSuccess
                                ? 'rgba(48,209,88,0.15)'
                                : hasChanges
                                    ? `linear-gradient(135deg, ${T.purple}, #a78bfa)`
                                    : T.surface,
                            border: `1px solid ${saveSuccess ? 'rgba(48,209,88,0.3)' : hasChanges ? 'rgba(129,140,248,0.35)' : T.border}`,
                            transition: 'all 0.2s',
                            boxShadow: hasChanges && !saveSuccess ? '0 2px 12px rgba(129,140,248,0.25)' : 'none',
                        }}
                    >
                        {saving ? (
                            <ActivityIndicator size="small" color="#fff" />
                        ) : saveSuccess ? (
                            <>
                                <FaCheckCircle size={11} color={T.success} />
                                <span style={{ color: T.success, fontSize: 12, fontWeight: 700 }}>{t('app.writingStyle.saved')}</span>
                            </>
                        ) : (
                            <>
                                <FaSave size={11} color={hasChanges ? '#fff' : T.textMuted} />
                                <span style={{ color: hasChanges ? '#fff' : T.textMuted, fontSize: 12, fontWeight: 700 }}>{t('app.writingStyle.save')}</span>
                            </>
                        )}
                    </button>
                </div>

                {/* ── Body ── */}
                <div style={{ flex: 1, overflowY: 'auto' }}>
                    <div style={{ padding: '24px 24px 48px', maxWidth: 700, margin: '0 auto' }}>

                        {/* Intro banner */}
                        <div style={{
                            background: `linear-gradient(135deg, rgba(129,140,248,0.08), rgba(167,139,250,0.04))`,
                            border: '1px solid rgba(129,140,248,0.18)',
                            borderRadius: 16, padding: '16px 20px', marginBottom: 28,
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                                <FaMagic size={12} color={T.purple} />
                                <span style={{ color: T.purple, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                                    {t('app.writingStyle.aiPersonalization')}
                                </span>
                            </div>
                            <div style={{ color: T.textSec, fontSize: 13, lineHeight: '20px' }}>
                                {t('app.writingStyle.aiPersonalizationDesc')}
                            </div>
                        </div>

                        {/* ── Context ── */}
                        <SectionLabel>{t('app.writingStyle.sectionContext')}</SectionLabel>
                        <div className="ws-field-card" style={{
                            background: T.card, borderRadius: 16, border: `1px solid ${T.border}`,
                            padding: '18px 20px', marginBottom: 20, transition: 'border-color 0.15s, box-shadow 0.15s',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, paddingBottom: 14, borderBottom: `1px solid ${T.border}` }}>
                                <div style={{ width: 30, height: 30, borderRadius: 9, background: 'rgba(10,132,255,0.1)', border: '1px solid rgba(10,132,255,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <FaUser size={12} color={T.primary} />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ color: T.text, fontSize: 13, fontWeight: 700 }}>{t('app.writingStyle.brandBackground')}</div>
                                    <div style={{ color: T.textMuted, fontSize: 11, marginTop: 1 }}>{t('app.writingStyle.brandBackgroundHint')}</div>
                                </div>
                                <span style={{ color: T.textMuted, fontSize: 10, fontFamily: 'monospace' }}>{context.length}</span>
                            </div>
                            <textarea
                                className="ws-textarea"
                                value={context}
                                onChange={e => { setContext(e.target.value); setHasChanges(true); }}
                                rows={6}
                                placeholder={t('app.writingStyle.contextPlaceholder')}
                            />
                        </div>

                        {/* ── Writing Style ── */}
                        <SectionLabel>{t('app.writingStyle.sectionVoice')}</SectionLabel>
                        <div className="ws-field-card" style={{
                            background: T.card, borderRadius: 16, border: `1px solid ${T.border}`,
                            padding: '18px 20px', marginBottom: 20, transition: 'border-color 0.15s, box-shadow 0.15s',
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, paddingBottom: 14, borderBottom: `1px solid ${T.border}` }}>
                                <div style={{ width: 30, height: 30, borderRadius: 9, background: 'rgba(129,140,248,0.1)', border: '1px solid rgba(129,140,248,0.18)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <FaPen size={12} color={T.purple} />
                                </div>
                                <div style={{ flex: 1 }}>
                                    <div style={{ color: T.text, fontSize: 13, fontWeight: 700 }}>{t('app.writingStyle.toneStyle')}</div>
                                    <div style={{ color: T.textMuted, fontSize: 11, marginTop: 1 }}>{t('app.writingStyle.toneStyleHint')}</div>
                                </div>
                                <span style={{ color: T.textMuted, fontSize: 10, fontFamily: 'monospace' }}>{writingStyle.length}</span>
                            </div>
                            <textarea
                                className="ws-textarea"
                                value={writingStyle}
                                onChange={e => { setWritingStyle(e.target.value); setHasChanges(true); }}
                                rows={5}
                                placeholder={t('app.writingStyle.voicePlaceholder')}
                            />
                        </div>

                        {/* ── Brand Scale ── */}
                        <SectionLabel>{t('app.writingStyle.sectionScale')}</SectionLabel>
                        <div style={{ display: 'flex', gap: 10, marginBottom: 28 }}>
                            {SCALE_OPTIONS.map(opt => {
                                const active = brandScale === opt.value;
                                return (
                                    <button
                                        key={opt.value}
                                        className={`ws-scale${active ? ' active' : ''}`}
                                        onClick={() => { setBrandScale(opt.value); setHasChanges(true); }}
                                        style={{
                                            flex: 1, padding: '16px 10px', borderRadius: 14, cursor: 'pointer',
                                            background: active ? `${opt.color}0e` : T.card,
                                            border: `1px solid ${active ? opt.color + '50' : T.border}`,
                                            textAlign: 'center', transition: 'all 0.15s',
                                        }}
                                    >
                                        <div style={{ fontSize: 22, marginBottom: 8 }}>{opt.icon}</div>
                                        <div style={{ color: active ? opt.color : T.text, fontSize: 13, fontWeight: 700, marginBottom: 3 }}>
                                            {opt.label}
                                        </div>
                                        <div style={{ color: T.textMuted, fontSize: 10, marginBottom: 10 }}>{opt.sub}</div>
                                        <div style={{
                                            display: 'inline-block',
                                            background: active ? `${opt.color}18` : T.surface,
                                            border: `1px solid ${active ? opt.color + '35' : T.border}`,
                                            borderRadius: 5, padding: '2px 8px',
                                        }}>
                                            <span style={{ color: active ? opt.color : T.textMuted, fontSize: 9, fontFamily: 'monospace', fontWeight: 700 }}>
                                                major: {opt.threshold}
                                            </span>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        {/* ── Tips ── */}
                        <div style={{
                            borderRadius: 16, border: '1px solid rgba(255,214,10,0.15)',
                            background: 'rgba(255,214,10,0.04)', overflow: 'hidden',
                        }}>
                            <div style={{ padding: '12px 18px', borderBottom: '1px solid rgba(255,214,10,0.1)', display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span style={{ fontSize: 14 }}>💡</span>
                                <span style={{ color: T.warning, fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8 }}>
                                    {t('app.writingStyle.tipsTitle')}
                                </span>
                            </div>
                            <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column', gap: 10 }}>
                                {[
                                    [t('app.writingStyle.tip1Title'), t('app.writingStyle.tip1Body')],
                                    [t('app.writingStyle.tip2Title'), t('app.writingStyle.tip2Body')],
                                    [t('app.writingStyle.tip3Title'), t('app.writingStyle.tip3Body')],
                                    [t('app.writingStyle.tip4Title'), t('app.writingStyle.tip4Body')],
                                ].map(([title, body]) => (
                                    <div key={title} style={{ display: 'flex', gap: 10 }}>
                                        <div style={{ width: 4, borderRadius: 2, background: 'rgba(255,214,10,0.35)', flexShrink: 0, marginTop: 2 }} />
                                        <div>
                                            <span style={{ color: T.text, fontSize: 12, fontWeight: 600 }}>{title} </span>
                                            <span style={{ color: T.textSec, fontSize: 12, lineHeight: '18px' }}>{body}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </>
    );
};

export default WritingStyleScreen;
